import React, { memo, useCallback, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { BasicCheckbox } from 'src/components/shared/form/BasicCheckbox'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { AdminFetchCustomers, AdminFetchMarkets } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getCustomers } from 'src/selectors/customer'
import { getMarkets } from 'src/selectors/market'
import { resolveCustomerDisplayName } from 'src/utils/customerList'

interface Props {
  partnerId: string | null
  partnerMarketId: string | null
  onlyScheduled: boolean
  onPartnerChange(partnerId: string | null): void
  onMarketChange(partnerMarketId: string | null): void
  onOnlyScheduledChange(onlyScheduled: boolean): void
  onRefresh(): void
}

/**
 * Fixed rather than `flex-1`: these two filters do not need to eat the toolbar's whole width, and
 * a partner name long enough to need more room is truncated by the trigger, not wrapped.
 *
 * `ProtoSelectStandalone`'s root is `flex-1`, so it fills this box. No height override here —
 * the control brings its own 38px trigger, and forcing it to something else would fight its
 * chrome.
 */
const filterControlWidthCss = tw`flex w-[220px] flex-col`

const filterLabelCss = tw`mb-1 text-shades-400`

/**
 * `BasicCheckbox` draws its own rounded border, which reads as a third input box next to the two
 * selects. Stripped here at the call site rather than in the shared component — every other
 * caller wants the border.
 */
const borderlessCheckboxCss = tw`[& .MuiFormControlLabel-root]:border-0 [& .MuiFormControlLabel-root]:p-0`

const ScheduledActionsToolbarComponent = ({
  partnerId,
  partnerMarketId,
  onlyScheduled,
  onPartnerChange,
  onMarketChange,
  onOnlyScheduledChange,
  onRefresh,
}: Props) => {
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const customers = useSelector(getCustomers)
  const markets = useSelector(getMarkets)

  // `ScheduledActionsTab` does not render this toolbar without `partners_view`; checked here as
  // well so the GET /partners call can never fire unpermitted. Markets are their own grant: the
  // filter is hidden without `markets_view` (spec §6).
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)
  const canViewMarkets = useHasPermission(InternalPermission.MarketsView)

  useEffect(() => {
    if (!isAuthenticated || !canViewPartners) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchCustomers.request())
  }, [asyncDispatchWithErrorToast, canViewPartners, isAuthenticated])

  // Markets are partner-scoped; refetched whenever the partner changes.
  useEffect(() => {
    if (!isAuthenticated || !canViewMarkets || !partnerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchMarkets.request({ partnerId }))
  }, [asyncDispatchWithErrorToast, canViewMarkets, isAuthenticated, partnerId])

  const partnerOptions = useMemo(() => {
    return customers.map((customer) => {
      return { value: customer.id, label: resolveCustomerDisplayName(customer) }
    })
  }, [customers])

  const marketOptions = useMemo(() => {
    return markets.map((market) => {
      return { value: market.id, label: market.title }
    })
  }, [markets])

  // Changing the partner invalidates the market: a market belongs to exactly one partner, and
  // carrying the old one over would filter the new partner's list to nothing. Clearing the
  // market is handled by `ScheduledActionsTab.handlePartnerChange`, not here — a second
  // `onMarketChange(null)` call in this handler would build its URL update from the same stale
  // `searchParams` closure as the `onPartnerChange` call above, so the two updates would race and
  // the market clear would win, discarding the partner change.
  const handlePartnerChange = useCallback(
    (value: string | number) => {
      const partnerValue = String(value).trim()

      onPartnerChange(partnerValue === '' ? null : partnerValue)
    },
    [onPartnerChange],
  )

  const handleMarketChange = useCallback(
    (value: string | number) => {
      const marketValue = String(value).trim()

      onMarketChange(marketValue === '' ? null : marketValue)
    },
    [onMarketChange],
  )

  const handleOnlyScheduledChange = useCallback(
    (event: React.ChangeEvent<any>) => {
      onOnlyScheduledChange(event.target.checked)
    },
    [onOnlyScheduledChange],
  )

  return (
    <div
      css={tw`flex flex-row flex-wrap items-end gap-4 border-0 border-b border-t border-solid border-shades-100 bg-shades-0 px-4 py-3`}
    >
      {/*
        `ProtoSelectStandalone` rather than `BasicSelect`: the account can hold hundreds of
        partners, and this control brings its own in-menu search. `searchable` is passed
        explicitly on both — left to its default it switches on only at 10+ options, so a partner
        with a handful of markets would get a search box on one filter and not the other.

        It has no `label` prop, so the label is rendered here, matching `BasicSelect`'s own
        `h6 / mb-1 / text-shades-400` treatment so the two read identically elsewhere in the app.
      */}
      <div data-testid="schedulers-partner-select" css={filterControlWidthCss}>
        <Typography variant="h6" css={filterLabelCss}>
          Partner
        </Typography>
        <ProtoSelectStandalone
          value={partnerId ?? ''}
          options={partnerOptions}
          placeholder="Select a partner"
          searchable
          searchPlaceholder="Search partners…"
          onValueChange={handlePartnerChange}
        />
      </div>
      {canViewMarkets ? (
        <div data-testid="schedulers-market-select" css={filterControlWidthCss}>
          <Typography variant="h6" css={filterLabelCss}>
            Market
          </Typography>
          <ProtoSelectStandalone
            value={partnerMarketId ?? ''}
            options={marketOptions}
            placeholder="Select a market"
            disabled={!partnerId}
            searchable
            searchPlaceholder="Search markets…"
            onValueChange={handleMarketChange}
          />
        </div>
      ) : null}
      {/* `pb-1` aligns the checkbox's text with the inputs' baseline, not the label row above. */}
      <div css={[borderlessCheckboxCss, tw`pb-1`]}>
        <BasicCheckbox
          name="schedulersOnlyScheduled"
          label="Show only scheduled"
          value={onlyScheduled}
          onChange={handleOnlyScheduledChange}
          inputProps={{ 'data-testid': 'schedulers-only-scheduled' }}
        />
      </div>
      <div css={tw`ml-auto pb-1`}>
        <Button
          size="small"
          color="white"
          leftIcon={<GSIcon iconName="gs_arrows_repeat" size={20} css={tw`text-shades-1000`} />}
          onClick={onRefresh}
          data-testid="schedulers-refresh-button"
        >
          Refresh
        </Button>
      </div>
    </div>
  )
}

export const ScheduledActionsToolbar = memo(ScheduledActionsToolbarComponent)
